import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  Phone, Save, TestTube2, Eye, EyeOff, Copy, Check, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Link2, Unplug, Info,
  Headphones, Smartphone, Trash2, Lock,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, AppBadge, ConfirmModal,
} from "../ui";
import "./TelephonySettings.scss";

/* ── Toggle switch ── */
function TelToggle({ checked, onChange, id, disabled }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`tel-toggle ${checked ? "tel-toggle--on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="tel-toggle__thumb" />
    </button>
  );
}

/* ── Password field with reveal ── */
function SecretInput({ value, onChange, placeholder, disabled }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="tel-secret-wrap">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="tel-input"
      />
      <button
        type="button"
        className="tel-secret-eye"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        tabIndex={-1}
      >
        {visible ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

/* ── Read-only URL with a copy button ── */
function CopyField({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the field is selectable, so this is recoverable */
    }
  };
  return (
    <div className="tel-copy-wrap">
      <input className="tel-input tel-input--mono" value={value} readOnly onFocus={(e) => e.target.select()} />
      <button type="button" className="tel-copy-btn" onClick={copy}>
        {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
      </button>
    </div>
  );
}

/**
 * Fill in a select's first option as its actual value.
 *
 * Without this the dropdown *renders* the first option while the form state
 * holds nothing, so the value is never submitted and the screen shows a choice
 * the server was never told about. For Exotel's cluster picker that means a
 * silent 401 the admin has no way to diagnose.
 */
function seedDefaults(providerMeta, credentials = {}) {
  const seeded = { ...(credentials || {}) };
  for (const field of providerMeta?.credentialFields || []) {
    if (field.type === "select" && !seeded[field.key]) {
      seeded[field.key] = field.options?.[0]?.value || "";
    }
  }
  return seeded;
}

function formatWhen(value) {
  if (!value) return "Never";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Per-agent SIP identity table.
 *
 * Lists **every** staff member, not only the ones who already have an identity —
 * the table is how an identity gets assigned, so a person with none has to appear
 * in it. Fetched only when browser calling is actually usable, since it is a
 * second round-trip that a phone-only institute never needs.
 *
 * ⚠️ Identities are provisioned **by the provider**, not from here. Exotel has no
 * documented self-serve way to mint one, so this screen stores what their
 * dashboard already created rather than pretending to create it.
 */
function AgentIdentityTable({ sipDomain, onToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get("/telephony/agents")
      .then(({ data }) => {
        setRows(data.agents || []);
        setDrafts(Object.fromEntries((data.agents || []).map((a) => [a.adminId, a.sipIdentity || ""])));
      })
      .catch(() => onToast({ show: true, message: "Failed to load counsellors.", type: "error" }))
      .finally(() => setLoading(false));
  }, [onToast]);

  useEffect(load, [load]);

  const save = async (row) => {
    setSavingId(row.adminId);
    try {
      const { data } = await apiClient.put(`/telephony/agents/${row.adminId}`, {
        sipIdentity: drafts[row.adminId] || "",
      });
      onToast({ show: true, message: data.message || "Saved.", type: "success" });
      load();
    } catch (ex) {
      onToast({
        show: true,
        message: ex?.response?.data?.message || "Could not save that identity.",
        type: "error",
      });
    }
    setSavingId("");
  };

  const remove = async () => {
    const row = confirmRemove;
    if (!row) return;
    try {
      const { data } = await apiClient.delete(`/telephony/agents/${row.adminId}`);
      onToast({ show: true, message: data.message, type: "success" });
      setConfirmRemove(null);
      load();
    } catch (ex) {
      onToast({
        show: true,
        message: ex?.response?.data?.message || "Could not remove browser calling.",
        type: "error",
      });
    }
  };

  if (loading) return <Shimmer height={40} count={4} />;

  return (
    <>
      <div className="tel-agents">
        <div className="tel-agents__head">
          <span>Counsellor</span>
          <span>SIP identity</span>
          <span>Status</span>
          <span />
        </div>

        {rows.map((row) => {
          const draft = drafts[row.adminId] ?? "";
          const dirty = draft.trim() !== (row.sipIdentity || "");
          return (
            <div className="tel-agents__row" key={row.adminId}>
              <div className="tel-agents__who">
                <span className="tel-agents__name">{row.name}</span>
                <span className="tel-agents__sub">{row.email}</span>
              </div>

              <div className="tel-agents__identity">
                <input
                  className="tel-input tel-input--mono"
                  value={draft}
                  placeholder="e.g. agent_001"
                  onChange={(e) => setDrafts((d) => ({ ...d, [row.adminId]: e.target.value }))}
                />
                {draft.trim() && sipDomain && (
                  <span className="tel-help">sip:{draft.trim().split("@")[0]}@{sipDomain}</span>
                )}
              </div>

              <div className="tel-agents__status">
                {row.sipIdentity ? (
                  <>
                    <AppBadge variant="secondary" color="emerald">Browser</AppBadge>
                    <span className="tel-help">
                      {row.lastRegisteredAt
                        ? `Last registered ${formatWhen(row.lastRegisteredAt)}`
                        : "Never registered"}
                    </span>
                  </>
                ) : (
                  <>
                    <AppBadge variant="secondary">Phone</AppBadge>
                    {/* No mobile AND no identity means this person cannot be
                        called at all — worth saying here rather than at dial time. */}
                    {!row.mobile && (
                      <span className="tel-help tel-agents__warn">No mobile number on file</span>
                    )}
                  </>
                )}
              </div>

              <div className="tel-agents__actions">
                <AppButton
                  size="sm"
                  variant={dirty ? "primary" : "secondary"}
                  disabled={!dirty || savingId === row.adminId}
                  onClick={() => save(row)}
                >
                  {savingId === row.adminId ? <Spinner animation="border" size="sm" /> : "Save"}
                </AppButton>
                {row.sipIdentity && (
                  <button
                    type="button"
                    className="tel-agents__remove"
                    title="Remove browser calling"
                    onClick={() => setConfirmRemove(row)}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {!rows.length && <p className="tel-help tel-help--block">No staff members to configure yet.</p>}
      </div>

      <ConfirmModal
        show={!!confirmRemove}
        onHide={() => setConfirmRemove(null)}
        onConfirm={remove}
        title="Remove browser calling"
        message={
          <>
            {confirmRemove?.name} will go back to having their own phone ring first.
            <strong> They can still make calls</strong> — only the browser option is removed.
          </>
        }
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
}

const TelephonySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const [providers, setProviders] = useState([]);
  const [server, setServer] = useState(null);
  const [form, setForm] = useState({
    provider: "",
    isActive: false,
    credentials: {},
    callerId: "",
    recordCalls: false,
    callTimeLimitSec: 3600,
    autoCreateEnquiryOnUnknownInbound: true,
    callMode: "phone",
    allowAgentOverride: true,
    sipDomain: "",
  });

  const activeProvider = providers.find((p) => p.name === form.provider) || null;

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get("/telephony/config")
      .then(({ data }) => {
        setProviders(data.providers || []);
        setServer(data);
        const providerName = data.provider || data.providers?.[0]?.name || "";
        const meta = (data.providers || []).find((p) => p.name === providerName);
        setForm({
          provider: providerName,
          isActive: data.isActive ?? false,
          // Secrets always start blank — an empty value means "keep what's stored".
          credentials: seedDefaults(meta, data.credentials),
          callerId: data.callerId || "",
          recordCalls: data.recordCalls ?? false,
          callTimeLimitSec: data.callTimeLimitSec ?? 3600,
          autoCreateEnquiryOnUnknownInbound: data.autoCreateEnquiryOnUnknownInbound ?? true,
          callMode: data.callMode || "phone",
          allowAgentOverride: data.allowAgentOverride ?? true,
          sipDomain: data.sipDomain || "",
        });
      })
      .catch(() => setToast({ show: true, message: "Failed to load telephony settings.", type: "error" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const updateField = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const updateCredential = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, credentials: { ...f.credentials, [key]: val } }));
  };

  const handleSave = async () => {
    if (!form.provider) {
      setToast({ show: true, message: "Choose a provider first.", type: "error" });
      return;
    }
    setSaving(true);
    try {
      // Blank secrets are dropped rather than sent, so saving the form without
      // retyping credentials doesn't wipe them.
      const credentials = {};
      for (const field of activeProvider?.credentialFields || []) {
        const value = form.credentials[field.key];
        if (field.secret && !value) continue;
        if (value !== undefined) credentials[field.key] = value;
      }
      const { data } = await apiClient.put("/telephony/config", { ...form, credentials });
      setToast({ show: true, message: data.message || "Saved.", type: "success" });
      load();
    } catch (ex) {
      setToast({
        show: true,
        message: ex?.response?.data?.message || "Failed to save telephony settings.",
        type: "error",
      });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/telephony/config/test");
      setTestResult({ success: true, message: data.message });
    } catch (ex) {
      setTestResult({
        success: false,
        message: ex?.response?.data?.message || "Connection test failed.",
      });
    }
    setTesting(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await apiClient.post("/telephony/config/regenerate-token");
      setToast({ show: true, message: data.message, type: "success" });
      setServer((s) => ({ ...s, webhookUrl: data.webhookUrl }));
    } catch (ex) {
      setToast({
        show: true,
        message: ex?.response?.data?.message || "Failed to regenerate the webhook URL.",
        type: "error",
      });
    }
    setRegenerating(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data } = await apiClient.delete("/telephony/config");
      setToast({ show: true, message: data.message, type: "success" });
      setConfirmDisconnect(false);
      load();
    } catch (ex) {
      setToast({
        show: true,
        message: ex?.response?.data?.message || "Failed to disconnect.",
        type: "error",
      });
    }
    setDisconnecting(false);
  };

  if (loading) {
    return (
      <div className="tel-settings">
        <Shimmer height={72} count={1} />
        <Shimmer height={240} count={2} />
      </div>
    );
  }

  const connected = !!server?.connected;
  const credentialsSet = server?.credentialsSet || {};
  const expiringSoon = server?.credentialExpiresAt
    && new Date(server.credentialExpiresAt) - Date.now() < 14 * 24 * 3600 * 1000;

  return (
    <div className="tel-settings">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      {/* ── Status bar ── */}
      <AppCard className="tel-status-card">
        <AppCardContent>
          <div className="tel-status-bar">
            <div className="tel-status-bar__left">
              <Phone size={16} className="tel-status-icon" />
              <span className="tel-status-label">Calling</span>
              <TelToggle
                id="tel-active"
                checked={form.isActive}
                onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
                disabled={!connected}
              />
              {connected
                ? <AppBadge variant="secondary" color={form.isActive ? "emerald" : undefined}>
                    {form.isActive ? "Active" : "Paused"}
                  </AppBadge>
                : <AppBadge variant="secondary">Not connected</AppBadge>}
            </div>
            <div className="tel-status-bar__right">
              {connected && (
                <AppButton variant="ghost" size="sm" onClick={() => setConfirmDisconnect(true)}>
                  <Unplug size={14} /> Disconnect
                </AppButton>
              )}
              <AppButton size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : <><Save size={14} /> Save</>}
              </AppButton>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── How this works ── */}
      <AppCard className="tel-explainer">
        <AppCardContent>
          <Info size={15} className="tel-explainer__icon" />
          <div>
            <strong>You bring your own calling account.</strong> Connect the IVR provider you
            already pay — we place calls through it and log them against each enquiry. Minutes are
            billed by your provider, not by us.
            {activeProvider?.whoRingsFirst === "agent" && (
              <> When a counsellor clicks <em>Call</em>, their own phone rings first; answering it
              dials the student. Neither side sees the other's personal number.</>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Provider + credentials ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Provider</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tel-grid">
            <div className="tel-field">
              <label className="tel-label" htmlFor="tel-provider">Cloud telephony provider</label>
              <select
                id="tel-provider"
                className="tel-input"
                value={form.provider}
                onChange={(e) => {
                  const name = e.target.value;
                  const meta = providers.find((p) => p.name === name);
                  // Credentials don't carry across providers, and the new one's
                  // selects need their defaults seeded the same way.
                  //
                  // The calling mode doesn't carry either: leaving it on "browser"
                  // while switching to a provider that has no WebRTC makes the
                  // next Save fail with an error about the calling mode, when all
                  // the admin did was change provider.
                  setForm((f) => ({
                    ...f,
                    provider: name,
                    credentials: seedDefaults(meta, {}),
                    callMode: meta?.supportsWebRTC ? f.callMode : "phone",
                  }));
                }}
              >
                <option value="">Select a provider…</option>
                {providers.map((p) => (
                  <option key={p.name} value={p.name}>{p.label}</option>
                ))}
              </select>
            </div>

            <div className="tel-field">
              <label className="tel-label" htmlFor="tel-callerid">Your virtual number (caller ID)</label>
              <input
                id="tel-callerid"
                className="tel-input"
                value={form.callerId}
                onChange={updateField("callerId")}
                placeholder="e.g. 08040001111"
              />
              <span className="tel-help">
                The number students will see. Must be a number registered on your provider account.
              </span>
            </div>

            {(activeProvider?.credentialFields || []).map((field) => (
              <div className="tel-field" key={field.key}>
                <label className="tel-label" htmlFor={`tel-cred-${field.key}`}>
                  {field.label}
                  {field.required && <span className="tel-req"> *</span>}
                  {field.secret && credentialsSet[`${field.key}Set`] && (
                    <AppBadge variant="secondary" color="emerald" size="sm" className="tel-saved-badge">Saved</AppBadge>
                  )}
                </label>

                {field.type === "select" ? (
                  <select
                    id={`tel-cred-${field.key}`}
                    className="tel-input"
                    value={form.credentials[field.key] ?? ""}
                    onChange={updateCredential(field.key)}
                  >
                    {(field.options || []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : field.secret ? (
                  <SecretInput
                    value={form.credentials[field.key] || ""}
                    onChange={updateCredential(field.key)}
                    placeholder={credentialsSet[`${field.key}Set`] ? "•••••••• (leave blank to keep)" : ""}
                  />
                ) : (
                  <input
                    id={`tel-cred-${field.key}`}
                    className="tel-input"
                    value={form.credentials[field.key] || ""}
                    onChange={updateCredential(field.key)}
                  />
                )}

                {field.help && <span className="tel-help">{field.help}</span>}
              </div>
            ))}
          </div>

          <div className="tel-actions">
            <AppButton variant="secondary" size="sm" onClick={handleTest} disabled={testing || !connected}>
              {testing ? <Spinner animation="border" size="sm" /> : <><TestTube2 size={14} /> Test connection</>}
            </AppButton>
            {testResult && (
              <span className={`tel-test-result ${testResult.success ? "is-ok" : "is-bad"}`}>
                {testResult.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {testResult.message}
              </span>
            )}
          </div>

          {expiringSoon && (
            <div className="tel-warning">
              <AlertTriangle size={14} />
              These credentials expire on {formatWhen(server.credentialExpiresAt)}. Renew them in your
              provider portal and save the new ones here, or calling will stop working.
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Webhook — the step people miss ── */}
      {connected && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><Link2 size={15} /> Webhook URL</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <p className="tel-webhook-intro">
              Paste this into your provider dashboard so call results reach us. Until you do, calls
              will still connect but nothing will be logged.
            </p>
            <CopyField value={server.webhookUrl || ""} />
            {activeProvider?.webhookSetupHint && (
              <p className="tel-help tel-help--block">{activeProvider.webhookSetupHint}</p>
            )}

            <div className="tel-health">
              <div className="tel-health__item">
                <span className="tel-health__label">Last event received</span>
                <span className={`tel-health__value ${server.lastWebhookAt ? "" : "is-muted"}`}>
                  {formatWhen(server.lastWebhookAt)}
                </span>
              </div>
              <div className="tel-health__item">
                <span className="tel-health__label">Last call placed</span>
                <span className={`tel-health__value ${server.lastCallAt ? "" : "is-muted"}`}>
                  {formatWhen(server.lastCallAt)}
                </span>
              </div>
              <div className="tel-health__item">
                <span className="tel-health__label">Total calls</span>
                <span className="tel-health__value">{server.totalCalls || 0}</span>
              </div>
            </div>

            {server.lastErrorMessage && (
              <div className="tel-warning">
                <AlertTriangle size={14} />
                Last error ({formatWhen(server.lastErrorAt)}): {server.lastErrorMessage}
              </div>
            )}

            <div className="tel-actions">
              <AppButton variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating}>
                {regenerating ? <Spinner animation="border" size="sm" /> : <><RefreshCw size={14} /> Regenerate URL</>}
              </AppButton>
              <span className="tel-help">
                Regenerating invalidates the old URL immediately — update your provider dashboard straight after.
              </span>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── How counsellors talk: their phone, or the browser ── */}
      {connected && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><Headphones size={15} /> How counsellors take calls</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {/* Disabled WITH a reason rather than hidden. An option that silently
                vanishes reads as a bug; one that explains itself does not. */}
            {!activeProvider?.supportsWebRTC && (
              <div className="tel-warning tel-warning--muted">
                <Lock size={14} />
                {activeProvider?.label || "This provider"} has no browser calling, so every call
                rings the counsellor's own phone. Switching provider is the only way to change that.
              </div>
            )}

            <div className="tel-modes">
              {[
                {
                  value: "phone",
                  icon: Smartphone,
                  title: "Their phone rings first",
                  blurb: "Works anywhere, on any handset, with no headset and no browser tab open. "
                    + "The right default for anyone moving around campus.",
                },
                {
                  value: "webrtc",
                  icon: Headphones,
                  title: "They talk in the browser",
                  blurb: "No mobile leg to answer — the call opens in the CRM. Needs a headset, "
                    + "microphone permission and a stable connection.",
                },
              ].map((opt) => {
                const disabled = opt.value === "webrtc" && !activeProvider?.supportsWebRTC;
                const selected = form.callMode === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    disabled={disabled}
                    className={`tel-mode ${selected ? "is-selected" : ""} ${disabled ? "is-disabled" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, callMode: opt.value }))}
                  >
                    <opt.icon size={16} className="tel-mode__icon" />
                    <span className="tel-mode__title">{opt.title}</span>
                    <span className="tel-mode__blurb">{opt.blurb}</span>
                  </button>
                );
              })}
            </div>

            <div className="tel-switch-row">
              <div>
                <span className="tel-switch-row__label">Let counsellors choose for themselves</span>
                <span className="tel-help">
                  Recommended. The right answer differs per person — a headset at a desk versus a
                  phone on the walk between batches — and forcing one on everyone makes calling
                  unusable for half a team.
                </span>
              </div>
              <TelToggle
                checked={form.allowAgentOverride}
                onChange={(v) => setForm((f) => ({ ...f, allowAgentOverride: v }))}
              />
            </div>

            {activeProvider?.supportsWebRTC && (
              <>
                {activeProvider.webrtcSetupHint && (
                  <p className="tel-help tel-help--block">{activeProvider.webrtcSetupHint}</p>
                )}

                <div className="tel-field tel-field--narrow tel-mode-domain">
                  <label className="tel-label" htmlFor="tel-sipdomain">SIP domain</label>
                  <input
                    id="tel-sipdomain"
                    className="tel-input tel-input--mono"
                    value={form.sipDomain}
                    onChange={updateField("sipDomain")}
                    placeholder="e.g. app.exotel.com"
                  />
                  <span className="tel-help">
                    From your provider. Every counsellor's identity is
                    {" "}<code>sip:their-identity@{form.sipDomain || "your-domain"}</code>.
                  </span>
                </div>

                <div className="tel-agents-block">
                  <h4 className="tel-subhead">Counsellor SIP identities</h4>
                  <p className="tel-help tel-help--block">
                    Your provider creates these — paste each one in. A counsellor with no identity
                    keeps using their phone, so you can roll this out one person at a time.
                  </p>
                  <AgentIdentityTable sipDomain={form.sipDomain} onToast={setToast} />
                </div>
              </>
            )}
          </AppCardContent>
        </AppCard>
      )}

      {/* ── Call behaviour ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Call behaviour</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="tel-switch-row">
            <div>
              <span className="tel-switch-row__label">Record calls</span>
              <span className="tel-help">
                Recordings are stored by your provider and may expire. Make sure you have consent to record.
              </span>
            </div>
            <TelToggle
              checked={form.recordCalls}
              onChange={(v) => setForm((f) => ({ ...f, recordCalls: v }))}
              disabled={!activeProvider?.supportsPerCallRecordToggle}
            />
          </div>
          {activeProvider && !activeProvider.supportsPerCallRecordToggle && (
            <p className="tel-help tel-help--block">
              {activeProvider.label} controls recording at the account level — turn it on in their dashboard.
            </p>
          )}

          <div className="tel-switch-row">
            <div>
              <span className="tel-switch-row__label">Create an enquiry for unknown callers</span>
              <span className="tel-help">
                When someone we don't recognise calls your number, add them as a new enquiry automatically.
              </span>
            </div>
            <TelToggle
              checked={form.autoCreateEnquiryOnUnknownInbound}
              onChange={(v) => setForm((f) => ({ ...f, autoCreateEnquiryOnUnknownInbound: v }))}
            />
          </div>

          <div className="tel-field tel-field--narrow">
            <label className="tel-label" htmlFor="tel-timelimit">Maximum call length (minutes)</label>
            <input
              id="tel-timelimit"
              type="number"
              min={1}
              max={Math.floor((activeProvider?.maxCallTimeLimitSec || 14400) / 60)}
              className="tel-input"
              value={Math.round((form.callTimeLimitSec || 3600) / 60)}
              onChange={(e) => setForm((f) => ({
                ...f,
                callTimeLimitSec: Math.max(60, (Number(e.target.value) || 1) * 60),
              }))}
            />
            <span className="tel-help">Calls are cut off after this, protecting you from runaway charges.</span>
          </div>
        </AppCardContent>
      </AppCard>

      <ConfirmModal
        show={confirmDisconnect}
        onHide={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect telephony"
        message={
          <>
            Remove your {activeProvider?.label || "telephony"} credentials? Counsellors will no longer
            be able to call from the CRM. <strong>Your existing call history is kept</strong>, but the
            SIP identities you assigned for browser calling are removed and will need to be entered
            again.
          </>
        }
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        loading={disconnecting}
      />
    </div>
  );
};

export default TelephonySettings;
